import AppBar from '@mui/material/AppBar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import type { Message } from '@stomp/stompjs';
import Image from 'next/image';
import TypingIndicatorPath from 'packages/workspace/public/Typing-bubble-left.gif';
import type { IChatMessage } from 'packages/workspace/services/models/chat-message';
import type { ChatConversationMessage } from 'packages/workspace/services/models/internal-channel';
import { MessageTopicsConstants } from 'packages/workspace/services/store/omnichannel/chat-store';
import useInternalConversationStore from 'packages/workspace/services/store/omnichannel/internal-conversation-store';
import { useCallback, useEffect, useState, useRef } from 'react';
import MessageBox from '../../omnichannel/message-box/MessageBox';
import MessageList from '../../omnichannel/message-list/MessageList';

interface InternalMessageComponentProps {
  conversation_id: string | undefined;
  user_id: string;
  display_name: string | undefined;
  conversation_messages: IChatMessage[] | undefined;
  fullHeight?: boolean;
  user_name: string;
}
export const InternalMessageComponent: React.FC<InternalMessageComponentProps> = ({ conversation_id, user_name, user_id, display_name, conversation_messages = [], fullHeight = true }) => {
  const [isTypingMessage, setIsTypingMessage] = useState<string>('');
  const internalConvState = useInternalConversationStore();
  const lastMessageRef = useRef<HTMLLIElement>();
  /**
   * Handle is Typing Notification
   * @param message
   */
  const onIsTypingNotificationReceived = useCallback(
    (message: Message) => {
      if (!user_id) return;
      const messageReceived = JSON.parse(message.body);
      const isTypingBody = JSON.parse(messageReceived.data);
      if (isTypingBody.id != user_id && isTypingBody.isTyping) {
        setIsTypingMessage(`${isTypingBody.user} is typing`);
      } else {
        setIsTypingMessage('');
      }
    },
    [user_id]
  );

  useEffect(() => {
    if (!internalConvState.client.connected) return;
    const activeChatSubs$ = internalConvState.client?.subscribe(`/topic/${MessageTopicsConstants.INTERNAL_CHAT_IS_TYPING}/${conversation_id}`, onIsTypingNotificationReceived);
    return () => {
      activeChatSubs$.unsubscribe();
      setIsTypingMessage('');
    };
  }, [conversation_id, internalConvState.client, onIsTypingNotificationReceived]);

  /**
   * Publishes Message
   * @param message
   */
  const sendMessage = (message: string) => {
    if (conversation_id) {
      const conversationMessage: ChatConversationMessage = {
        sender: user_id,
        conversation_id,
        timestamp: 0,
        message,
        is_read: false,
        display_name: user_name,
      };
      internalConvState.client.publish({
        destination: `/app${MessageTopicsConstants.INTERNAL_CHAT}/${conversation_id}`,
        body: JSON.stringify(conversationMessage),
      });
    }
  };
  /**
   * Publish Is TYping
   */
  const broadcastTyping = useCallback(
    (isTyping: boolean) => {
      if (!internalConvState.client) return;
      internalConvState.client.publish({
        destination: `/app/${MessageTopicsConstants.INTERNAL_CHAT_IS_TYPING}/${conversation_id}`,
        body: JSON.stringify({
          id: user_id,
          user: user_name,
          isTyping,
        }),
      });
    },
    [internalConvState.client, user_id, user_name, conversation_id]
  );

  const scrollToBottom = () => lastMessageRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });

  useEffect(() => {
    scrollToBottom();
  }, []);

  return (
    <>
      {conversation_id && internalConvState.client ? (
        <div className={`flex flex-col h-full justify-between custom-scroll-pane ${fullHeight && 'chat-message-container'}`}>
          <Box>
            <AppBar position="static">{display_name}</AppBar>
          </Box>
          <MessageList
            conversation_id={conversation_id}
            messages={conversation_messages as unknown as ChatConversationMessage[]}
            userId={user_id}
            type="onetoone"
            lastMessageRef={lastMessageRef}
            fromInternalChat={true}
          />

          {isTypingMessage !== '' && (
            <div className="ml-2">
              <div className="flex items-center gap-2">
                <Badge className="badge" variant="dot" color="primary" />
                <span className="text-xs font-interbold">{isTypingMessage}</span>
              </div>
              <Image src={TypingIndicatorPath} alt={isTypingMessage} />
            </div>
          )}
          <MessageBox sendMessageProp={sendMessage} onTyping={broadcastTyping} />
        </div>
      ) : (
        <>Loading ... </>
      )}
    </>
  );
};
